import * as React from 'react'

import {
  AGGREGATION_SKILL,
  AGGREGATION_STUDENT,
  ENTITY_LABEL,
  ResultRow,
  getSortableEntityName,
} from './helpers'

import {
  buildMetricHeaders,
  buildMetricRowCells,
  buildNameHeader,
} from '../../adminMomentumReports/performanceBreakdown'
import { DataTable, DataTableChip, } from '../../../Shared/index'
import useScaledTableColumnWidths from '../../../Shared/hooks/useScaledTableColumnWidths'
import { useStickyTableHeader } from '../../../Shared/hooks/useStickyTableHeader'

interface PerformanceBreakdownViewProps {
  rows: ResultRow[];
  aggregation: string;
  goal: number;
  hasSelectedSkillCategory: boolean;
  onSelectSkillCategory: (skillId: number | string, skillName: string) => void;
  onSelectStudent: (studentId: number | string, studentName: string) => void;
}

const COLUMN_WIDTHS = [
  { width: 300, minWidth: 170 }, // Name (skill/grade/teacher/student)
  { width: 200, minWidth: 140 }, // Smart Group
  { width: 200, minWidth: 140 }, // (Avg) Momentum Score
  { width: 200, minWidth: 140 }, // (Avg) Momentum Score Trend
  { width: 200, minWidth: 116 }, // (Avg) Exposure
  { width: 200, minWidth: 140 }, // (Avg) Questions Answered
  { width: 200, minWidth: 116 }, // (Avg) Replay Rate
  { width: 200, minWidth: 116 }, // (Avg) Time Spent
  { width: 250, minWidth: 150 }, // Number of Students Practiced / Last Practiced
]

const PerformanceBreakdownView: React.FC<PerformanceBreakdownViewProps> = ({ rows, aggregation, goal, hasSelectedSkillCategory, onSelectSkillCategory, onSelectStudent, }) => {
  const containerRef = React.useRef<HTMLDivElement>(null)
  const wrapperRef = React.useRef<HTMLDivElement>(null)
  const tableRef = React.useRef<HTMLTableElement>(null)

  const scaledWidths = useScaledTableColumnWidths({
    columnWidths: COLUMN_WIDTHS,
    containerRef,
    maxTableWidth: 2000,
    minTableWidth: 1200,
    // containerRef sits on a div inside <main>, so its offsetWidth already excludes
    // main's 48px+48px padding. Only a small ~3px reservation is needed (border/scrollbar).
    additionalWidthRestrictions: 3,
  })

  useStickyTableHeader({
    wrapperRef,
    tableRef,
    enabled: true,
    dependencies: [rows, aggregation],
  })

  const isStudent = aggregation === AGGREGATION_STUDENT
  const isSkill = aggregation === AGGREGATION_SKILL
  const isSkillCategoryDrillIn = isSkill && !hasSelectedSkillCategory
  const labels = ENTITY_LABEL[aggregation]

  // Skill rows drill into the category filter (top-level only — once a category
  // is selected, rows are sub-categories and not interactive). Student rows
  // switch to Skill grouping pinned to that student.
  const handleNameClick = React.useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    const { rowId, rowName } = event.currentTarget.dataset
    if (!rowId || !rowName) return
    if (isSkillCategoryDrillIn) {
      onSelectSkillCategory(rowId, rowName)
    } else if (isStudent) {
      onSelectStudent(rowId, rowName)
    }
  }, [isSkillCategoryDrillIn, isStudent, onSelectSkillCategory, onSelectStudent])

  const widths = {
    name: scaledWidths['300w-170min'],
    smartGroup: scaledWidths['200w-140min'],
    momentumScore: scaledWidths['200w-140min'],
    trend: scaledWidths['200w-140min'],
    exposure: scaledWidths['200w-116min'],
    questionsAnswered: scaledWidths['200w-140min'],
    replayRate: scaledWidths['200w-116min'],
    timeSpent: scaledWidths['200w-116min'],
    finalColumn: scaledWidths['250w-150min'],
  }

  const headers = [
    buildNameHeader({ widths, entityLabel: labels }),
    ...buildMetricHeaders({
      widths,
      entityLabel: labels,
      goal,
      individualMetrics: isStudent,
      aggregateTooltips: {
        momentumScore: `The average Momentum Score across the students in this ${labels.singular}.`,
        trend: `The average performance trajectory across the students in this ${labels.singular}.`,
        exposure: `The average amount of practice across the students in this ${labels.singular}. Higher average exposure increases the reliability of the average Momentum Score.`,
        questionsAnswered: `The average number of questions answered per student in this ${labels.singular}.`,
        replayRate: `The average frequency with which students in this ${labels.singular} revisit questions.`,
        timeSpent: `The average time students in this ${labels.singular} have spent answering questions.`,
        numberOfStudents: `The number of students in this ${labels.singular} who have answered at least one question.`,
      },
    }),
  ]

  const tableRows = React.useMemo(() => rows.map((row) => {
    const { aggregate_id, name, } = row
    const interactiveName = isSkillCategoryDrillIn || isStudent

    return {
      id: aggregate_id,
      name: interactiveName ? (
        <button
          className="interactive-wrapper focus-on-light"
          data-row-id={aggregate_id}
          data-row-name={name}
          onClick={handleNameClick}
          type="button"
        >
          <DataTableChip color="green" label={name} />
        </button>
      ) : name,
      ...buildMetricRowCells(row, goal, getSortableEntityName(aggregation, name)),
    }
  }), [rows, aggregation, goal, isSkillCategoryDrillIn, isStudent, handleNameClick])

  return (
    <div className="admin-skills-performance-breakdown" ref={containerRef}>
      <div className="data-table-wrapper" ref={wrapperRef}>
        <DataTable
          defaultSortAttribute="smartGroupForSort"
          headers={headers}
          reportingFormat={true}
          rows={tableRows}
          showCheckboxes={false}
          tableRef={tableRef}
        />
      </div>
    </div>
  )
}

export default PerformanceBreakdownView
